import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface RentalCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useRentalCategories = () => {
  return useQuery({
    queryKey: ['rental-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rental_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data as RentalCategory[];
    },
  });
};

export const useAdminRentalCategories = () => {
  return useQuery({
    queryKey: ['admin-rental-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rental_categories')
        .select('*')
        .order('sort_order');

      if (error) throw error;
      return data as RentalCategory[];
    },
  });
};

export const useCreateRentalCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (category: Omit<RentalCategory, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('rental_categories')
        .insert(category)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-categories'] });
      toast.success('Category created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create category');
      console.error('Error creating category:', error);
    },
  });
};

export const useUpdateRentalCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<RentalCategory> }) => {
      const { data, error } = await supabase
        .from('rental_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-categories'] });
      toast.success('Category updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update category');
      console.error('Error updating category:', error);
    },
  });
};

export const useDeleteRentalCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('rental_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rental-categories'] });
      toast.success('Category deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete category');
      console.error('Error deleting category:', error);
    },
  });
};